/**
 * Unified toast notification component with AnimatePresence
 * Supports success/error/info/warning variants with Bootstrap colors
 */

import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useToast, ToastVariant } from "../../../hooks/useToast";

// Bootstrap alert colors for variants (all with white text for dark backgrounds)
const VARIANT_COLORS: Record<
  ToastVariant,
  { bg: string; text: string; border: string }
> = {
  success: {
    bg: "rgba(25, 135, 84, 0.95)",
    text: "#ffffff",
    border: "#198754",
  },
  error: {
    bg: "rgba(220, 53, 69, 0.95)",
    text: "#ffffff",
    border: "#dc3545",
  },
  info: {
    bg: "rgba(13, 202, 240, 0.95)",
    text: "#ffffff",
    border: "#0dcaf0",
  },
  warning: {
    bg: "rgba(255, 193, 7, 0.95)",
    text: "#ffffff",
    border: "#ffc107",
  },
};

const NavToast: React.FC = () => {
  const { current, dismiss } = useToast();

  if (!current) return null;

  const colors = VARIANT_COLORS[current.variant];

  return (
    <AnimatePresence>
      <motion.div
        key={current.id}
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        transition={{
          duration: 0.2,
          ease: [0.22, 1, 0.36, 1],
        }}
        role="status"
        aria-live="polite"
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 9999,
          background: colors.bg,
          color: colors.text,
          padding: "0.75rem 1rem",
          borderRadius: 8,
          border: `2px solid ${colors.border}`,
          boxShadow: "0 10px 30px rgba(0,0,0,0.6)",
          maxWidth: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "0.75rem",
        }}
      >
        <span style={{ textShadow: "0 1px 2px rgba(0,0,0,0.3)" }}>
          {current.message}
        </span>
        <button
          onClick={dismiss}
          style={{
            background: "transparent",
            border: "none",
            color: colors.text,
            cursor: "pointer",
            fontSize: "1.25rem",
            lineHeight: 1,
            padding: 0,
            opacity: 0.7,
          }}
          aria-label="Dismiss"
        >
          ×
        </button>
      </motion.div>
    </AnimatePresence>
  );
};

export default NavToast;
